// Effets visuels rejoués à chaque page affichée : défilement doux vers les
// ancres, apparition des cartes au défilement, compteurs animés.
//
// Appelé par <PageEffects> (AppShell) après le rendu de chaque page ; renvoie
// de quoi tout défaire avant la suivante.

let stylesInstalled = false

function installStyles() {
  if (stylesInstalled) return
  stylesInstalled = true
  const style = document.createElement('style')
  style.textContent = `
    .animate-in {
      opacity: 1 !important;
      transform: translateY(0) !important;
    }
  
    .floating {
      animation: float 6s ease-in-out infinite;
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
  
    .section-revealed {
      opacity: 1 !important;
      transform: translateY(0) !important;
    }
  `
  document.head.appendChild(style)
}

export function runPageEffects(root: ParentNode = document): () => void {
  installStyles()
  const cleanups: Array<() => void> = []

  // Défilement doux vers les ancres de la page.
  root.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((link) => {
    const onClick = (e: Event) => {
      const targetId = link.getAttribute('href')
      if (!targetId || targetId === '#') return
      e.preventDefault()
      document.querySelector(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    link.addEventListener('click', onClick)
    cleanups.push(() => link.removeEventListener('click', onClick))
  })

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) entry.target.classList.add('animate-in')
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' })
  root.querySelectorAll<HTMLElement>('.feature-card, .step-card').forEach((el) => {
    el.style.opacity = '0'
    el.style.transform = 'translateY(30px)'
    el.style.transition = 'opacity 0.6s ease, transform 0.6s ease'
    observer.observe(el)
  })
  cleanups.push(() => observer.disconnect())

  const hero = root.querySelector<HTMLElement>('.hero-section')
  if (hero && !document.querySelector('.landing-v3')) {
    const onScroll = () => { hero.style.transform = `translateY(${window.pageYOffset * 0.5}px)` }
    window.addEventListener('scroll', onScroll)
    cleanups.push(() => window.removeEventListener('scroll', onScroll))
  }

  const newsCards = root.querySelectorAll<HTMLElement>('.news-card')
  newsCards.forEach((card, index) => {
    const enter = () => { card.style.transform = 'translateY(-10px) scale(1.02)' }
    const leave = () => { card.style.transform = 'translateY(0) scale(1)' }
    card.addEventListener('mouseenter', enter)
    card.addEventListener('mouseleave', leave)
    cleanups.push(() => { card.removeEventListener('mouseenter', enter); card.removeEventListener('mouseleave', leave) })
    card.style.animationDelay = `${index * 0.5}s`
    card.classList.add('floating')
  })

  const timers: number[] = []
  const counterObserver = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return
      const target = entry.target as HTMLElement
      const finalValue = target.textContent || ''
      const match = finalValue.match(/^([0-9.]+)(.*)$/)
      if (match) {
        const numericValue = parseFloat(match[1])
        const suffix = match[2]
        const increment = numericValue / (2000 / 16)
        let current = 0
        const counter = window.setInterval(() => {
          current += increment
          if (current >= numericValue) {
            target.textContent = finalValue
            window.clearInterval(counter)
          } else {
            target.textContent = current.toFixed(1) + suffix
          }
        }, 16)
        timers.push(counter)
      }
      obs.unobserve(target)
    })
  }, { threshold: 0.5 })
  root.querySelectorAll('.stat-number').forEach((stat) => counterObserver.observe(stat))
  cleanups.push(() => { counterObserver.disconnect(); timers.forEach((id) => window.clearInterval(id)) })

  const heroTitle = root.querySelector<HTMLElement>('.hero-title .gradient-text')
  if (heroTitle) {
    const text = heroTitle.textContent || ''
    heroTitle.textContent = ''
    let i = 0
    const type = () => {
      if (i < text.length) {
        heroTitle.textContent += text.charAt(i)
        i++
        timers.push(window.setTimeout(type, 100))
      }
    }
    timers.push(window.setTimeout(type, 500))
  }

  const particles = root.querySelector('.hero-particles')
  if (particles) {
    for (let i = 0; i < 50; i++) {
      const p = document.createElement('div')
      p.style.position = 'absolute'
      p.style.width = Math.random() * 4 + 2 + 'px'
      p.style.height = p.style.width
      p.style.background = 'rgba(255, 255, 255, 0.1)'
      p.style.borderRadius = '50%'
      p.style.left = Math.random() * 100 + '%'
      p.style.top = Math.random() * 100 + '%'
      p.style.animation = `float ${Math.random() * 10 + 10}s linear infinite`
      p.style.animationDelay = Math.random() * 10 + 's'
      particles.appendChild(p)
    }
  }

  // Révélation des sections au défilement (hors landing v3).
  const sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) entry.target.classList.add('section-revealed')
    })
  }, { threshold: 0.1 })
  root.querySelectorAll<HTMLElement>('section:not([class*="v3-"])').forEach((section) => {
    if (section.closest('.landing-v3')) return
    section.style.opacity = '0'
    section.style.transform = 'translateY(50px)'
    section.style.transition = 'opacity 0.8s ease, transform 0.8s ease'
    sectionObserver.observe(section)
  })
  cleanups.push(() => sectionObserver.disconnect())

  return () => cleanups.forEach((fn) => fn())
}
